import React, { useState } from 'react';
import { Modal, Input, Form, message, Tag } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

const EmailModal = ({
  visible,
  title = 'Send To Email',
  onSubmit,
  onCancel,
  okText = 'Submit',
  cancelText = 'Cancel',
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [emails, setEmails] = useState([]);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setError('');
  };

  const handleKeyDown = (e) => {
    if (['Enter', 'Tab', ',', ' '].includes(e.key)) {
      e.preventDefault();
      addEmail();
    }
  };

  const addEmail = () => {
    if (!inputValue) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmail = emailRegex.test(inputValue.trim());

    if (!isEmail) {
      setError('Please enter a valid email address');
      return;
    }

    if (emails.includes(inputValue.trim())) {
      setError('This email is already added');
      return;
    }

    setEmails([...emails, inputValue.trim()]);
    setInputValue('');
    form.setFieldsValue({ email: '' });
  };

  const removeEmail = (emailToRemove) => {
    setEmails(emails.filter(email => email !== emailToRemove));
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    const pastedEmails = pasteData.split(/[,;\s]+/).filter(email => email.trim() !== '');
    
    if (pastedEmails.length > 0) {
      const validEmails = pastedEmails.filter(email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
      const newEmails = [...new Set([...emails, ...validEmails])];
      setEmails(newEmails);
    }
  };

  const handleBlur = () => {
    addEmail();
  };

  const handleOk = async () => {
    try {
      if (emails.length === 0) {
        setError('Please add at least one email address');
        return;
      }
      
      setLoading(true);
      await onSubmit(emails);
      setEmails([]);
      form.resetFields();
    } catch (error) {
      message.error('Failed to send emails');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEmails([]);
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      visible={visible}
      title={title}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={okText}
      cancelText={cancelText}
      confirmLoading={loading}
      bodyStyle={{
        padding: '24px',
      }}
    >
      <Form form={form} layout="vertical" name="email_form">
        <Form.Item
          name="email"
          validateStatus={error ? 'error' : ''}
          help={error}
          style={{ marginBottom: '16px' }}
        >
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            padding: '4px 11px',
            border: error ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
            borderRadius: '6px',
            minHeight: '32px',
            alignItems: 'center',
            backgroundColor: '#fff',
            transition: 'all 0.3s',
            ':hover': {
              borderColor: error ? '#ff4d4f' : '#40a9ff',
            },
            ':focus-within': {
              borderColor: error ? '#ff4d4f' : '#40a9ff',
              boxShadow: error ? '0 0 0 2px rgba(255, 77, 79, 0.2)' : '0 0 0 2px rgba(24, 144, 255, 0.2)',
              borderRightWidth: '1px',
              outline: '0',
            }
          }}>
            {emails.map((email) => (
              <Tag
                key={email}
                closable
                onClose={() => removeEmail(email)}
                closeIcon={<CloseOutlined style={{ fontSize: '12px' }} />}
                style={{
                  margin: 0,
                  padding: '0 7px',
                  height: '24px',
                  lineHeight: '22px',
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                }}
              >
                {email}
              </Tag>
            ))}
            <Input
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={handleBlur}
              placeholder={emails.length === 0 ? "Type or paste email addresses" : ""}
              bordered={false}
              style={{
                flex: 1,
                minWidth: '100px',
                height: '24px',
                padding: 0,
                boxShadow: 'none',
              }}
            />
          </div>
        </Form.Item>
       
      </Form>
    </Modal>
  );
};

export default EmailModal;